import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import "./WaitlistPage.css";

export default function WaitlistPage() {
  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    email: "",
    userType: "",
    businessType: "",
    participatedBefore: "",
    biggestChallenge: "",
    contributionAmount: "",
    wouldUseToday: "",
    priorityAccess: false
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const formRef = useRef<HTMLDivElement>(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSubmit = new FormData();
      // Add timestamp first in case their Sheet script iterates order-dependently
      dataToSubmit.append("timestamp", new Date().toISOString());

      Object.entries(formData).forEach(([key, value]) => {
        dataToSubmit.append(key, value.toString());
      });

      const scriptUrl = process.env.REACT_APP_GOOGLE_SHEETS_URL;

      if (scriptUrl) {
        await fetch(scriptUrl, {
          method: "POST",
          body: dataToSubmit,
          mode: "no-cors",
        });
      }

      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting form", error);
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="waitlist-wrapper">
      <div className="wl-bg-glow"></div>

      <nav className="wl-nav">
        <Link to="/" className="wl-logo">
          <img src="/cirupay-logo-compact.svg" alt="CiruPay Logo" />
        </Link>
      </nav>

      <main className="wl-container">

        {/* HERO SECTION */}
        <section className="wl-hero">
          <div className="wl-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14"></path>
              <path d="m12 5 7 7-7 7"></path>
            </svg>
            Early Access Programm
          </div>

          <h1 className="wl-h1">
            Turn your circle into a smarter way to <br />
            <span className="wl-h1-accent">save and get paid.</span>
          </h1>

          <p className="wl-subhead">
            CiruPay helps you join trusted savings circles, contribute easily, and receive your payout — all in one place.
          </p>

          <div className="wl-bullets">
            <div className="wl-bullet-item">
              <div className="wl-bullet-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              No chasing people for payments
            </div>
            <div className="wl-bullet-item">
              <div className="wl-bullet-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              No cash handling or risk
            </div>
            <div className="wl-bullet-item">
              <div className="wl-bullet-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              Structured, reliable payouts
            </div>
          </div>

          <button onClick={scrollToForm} className="wl-cta-btn">
            Join Early Access
          </button>

          {/* TRUST ANCHOR */}
          <div className="wl-trust-anchor">
            Built for vendors, small business owners, and communities that already save together — now with structure and security.
          </div>
        </section>

        {/* FORM SECTION */}
        <section ref={formRef} className="wl-form-section">
          <div className="wl-form-card">
            {submitted ? (
              <div className="wl-success-state">
                <div className="wl-success-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <h2 className="wl-success-title">You're on the list!</h2>
                <p className="wl-success-text">
                  Thank you for joining. We will notify you as soon as your priority access is ready.
                </p>
                <Link to="/" className="wl-cta-btn" style={{ display: 'inline-flex' }}>
                  Return to Home
                </Link>
              </div>
            ) : (
              <>
                <div className="wl-form-header">
                  <h2 className="wl-form-title">Join the CiruPay Early Access List</h2>
                  <p className="wl-form-subtitle">Get early access before public launch.</p>
                </div>
                <form onSubmit={handleSubmit} className="wl-form-grid">
                  <div className="wl-form-grid wl-form-grid-2">
                    <div className="wl-form-group">
                      <label className="wl-label">1. Full Name</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className="wl-input"
                        placeholder="John Doe"
                        required
                      />
                    </div>

                    <div className="wl-form-group">
                      <label className="wl-label">2. Phone Number</label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        className="wl-input"
                        placeholder="(123) 456-7890"
                        required
                      />
                    </div>
                  </div>

                  <div className="wl-form-group">
                    <label className="wl-label">3. Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="wl-input"
                      placeholder="you@email.com"
                      required
                    />
                  </div>

                  <div className="wl-form-grid wl-form-grid-2">
                    <div className="wl-form-group">
                      <label className="wl-label">4. What best describes you?</label>
                      <select
                        name="userType"
                        value={formData.userType}
                        onChange={handleChange}
                        className="wl-select"
                        required
                      >
                        <option value="" disabled>Select an option</option>
                        <option value="Business Owner">Business Owner</option>
                        <option value="Vendor">Vendor</option>
                        <option value="Freelancer">Freelancer</option>
                        <option value="Employee">Employee</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="wl-form-group">
                      <label className="wl-label">5. Type of business (if applicable)</label>
                      <select
                        name="businessType"
                        value={formData.businessType}
                        onChange={handleChange}
                        className="wl-select"
                      >
                        <option value="">N/A</option>
                        <option value="Food / Catering">Food / Catering</option>
                        <option value="Retail / Products">Retail / Products</option>
                        <option value="Services">Services</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="wl-form-group">
                    <label className="wl-label">6. Have you ever participated in a savings circle (Ajo, Esusu, Susu, etc.)?</label>
                    <div className="wl-radio-group">
                      <label className="wl-radio-label">
                        <input type="radio" name="participatedBefore" value="Yes" onChange={handleChange} className="wl-radio-input" required />
                        <span>Yes</span>
                      </label>
                      <label className="wl-radio-label">
                        <input type="radio" name="participatedBefore" value="No" onChange={handleChange} className="wl-radio-input" required />
                        <span>No</span>
                      </label>
                    </div>
                  </div>

                  <div className="wl-form-group">
                    <label className="wl-label">7. What is your biggest challenge when saving or accessing money?</label>
                    <input
                      type="text"
                      name="biggestChallenge"
                      value={formData.biggestChallenge}
                      onChange={handleChange}
                      className="wl-input"
                      placeholder="Optional"
                    />
                  </div>

                  <div className="wl-form-grid wl-form-grid-2">
                    <div className="wl-form-group">
                      <label className="wl-label">8. How much would you be comfortable contributing monthly?</label>
                      <select
                        name="contributionAmount"
                        value={formData.contributionAmount}
                        onChange={handleChange}
                        className="wl-select"
                        required
                      >
                        <option value="" disabled>Select amount</option>
                        <option value="$100 - $500">$100 – $500</option>
                        <option value="$500 - $1,000">$500 – $1,000</option>
                        <option value="$1,000+">$1,000+</option>
                      </select>
                    </div>
                  </div>

                  <div className="wl-form-group">
                    <label className="wl-label">9. If CiruPay were available today, would you use it?</label>
                    <div className="wl-radio-group">
                      <label className="wl-radio-label">
                        <input type="radio" name="wouldUseToday" value="Yes" onChange={handleChange} className="wl-radio-input" required />
                        <span>Yes</span>
                      </label>
                      <label className="wl-radio-label">
                        <input type="radio" name="wouldUseToday" value="Maybe" onChange={handleChange} className="wl-radio-input" required />
                        <span>Maybe</span>
                      </label>
                      <label className="wl-radio-label">
                        <input type="radio" name="wouldUseToday" value="No" onChange={handleChange} className="wl-radio-input" required />
                        <span>No</span>
                      </label>
                    </div>
                  </div>

                  <div className="wl-checkbox-group">
                    <input
                      type="checkbox"
                      id="priorityAccess"
                      name="priorityAccess"
                      checked={formData.priorityAccess}
                      onChange={handleChange}
                      className="wl-checkbox-input"
                    />
                    <label htmlFor="priorityAccess" className="wl-checkbox-label">
                      10. Yes, give me priority early access when we launch
                    </label>
                  </div>

                  <button type="submit" className="wl-submit-btn" disabled={loading}>
                    {loading ? (
                      <>
                        <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      "Join the Waitlist"
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </section>

        {/* SOCIAL PROOF SECTION */}
        <section className="wl-social-proof">
          <div className="wl-social-text">
            <div className="wl-avatars">
              <div className="wl-avatar" style={{ backgroundImage: "url('https://i.pravatar.cc/100?img=33')", backgroundSize: "cover" }}></div>
              <div className="wl-avatar" style={{ backgroundImage: "url('https://i.pravatar.cc/100?img=47')", backgroundSize: "cover" }}></div>
              <div className="wl-avatar" style={{ backgroundImage: "url('https://i.pravatar.cc/100?img=12')", backgroundSize: "cover" }}></div>
              <div className="wl-avatar" style={{ backgroundImage: "url('https://i.pravatar.cc/100?img=41')", backgroundSize: "cover" }}></div>
              <div className="wl-avatar">2k+</div>
            </div>
            Early users are already joining from local markets and small businesses.
          </div>
        </section>

      </main>

      {/* FOOTER */}
      <footer className="wl-footer">
        <div className="wl-footer-content">
          <div className="wl-footer-logo">
            <img src="/cirupay-logo-compact.svg" alt="CiruPay Logo" />
          </div>
          <div className="wl-footer-text">
            © 2026 CiruPay, Inc.
          </div>
          <div className="wl-footer-links">
            <Link to="#" className="wl-footer-link">Privacy Policy</Link>
            <Link to="#" className="wl-footer-link">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
